/**
 * `<ts-wishlist-button>` — Swym add/remove-from-wishlist toggle.
 *
 * Used on the PDP (wraps a `ts-button`) and on cart line items (own icon+label
 * markup). Product data + labels come from data-* attributes
 * (blocks/ts-wishlist-button.liquid, cart-products.liquid).
 *
 * On cart lines the click is a MOVE: add to the wishlist, then remove that
 * line from the cart (a Swym failure leaves it in place). Move mode is opted
 * in by Liquid via `data-ts-move="<line key>"`, emitted only on lines the
 * shopper may remove (`item.instructions.can_remove`) — elsewhere the button
 * is the plain add/remove toggle. In move mode there is no remove state: both
 * labels read "Move to Wishlist", and an already-wishlisted item is re-added
 * (idempotent) so Swym confirms membership before the cart line is dropped.
 * The key is captured at click time — positional line numbers go stale when a
 * concurrent morph renumbers the cart.
 *
 * Membership is matched on the Swym variant id (`epi`) and re-fetched whenever
 * the button (re)appears — connect, cart update, mini-cart open, variant change —
 * never preloaded (Swym's list isn't ready at page load). Triggers are coalesced
 * into one shared fetch per frame, serving every button on the page.
 */

import { ThemeEvents } from '@theme/events';
import { whenSwymReady, offSwymReady, SWYM_ADDED_EVENT, SWYM_REMOVED_EVENT, SWYM_ERROR_EVENT } from '@ts/utility-swym';

/**
 * Swym product payload — `empi`/`epi` identify the item; the rest is display metadata.
 * @typedef {Object} SwymProduct
 * @property {number} empi - Shopify product id.
 * @property {number} epi - Shopify variant id.
 * @property {string} du - Absolute product url.
 * @property {string} [dt] - Product title.
 * @property {string} [iu] - Image url.
 * @property {number} [pr] - Price in the store's major currency unit.
 */

/**
 * `epi`s known to be in the wishlist, shared by every button. Fetches only ADD
 * here — Swym can return a stale/partial list, so an omission is not "removed".
 * Only an explicit remove-click deletes.
 * @type {Set<number>}
 */
const inWishlist = new Set();

class TSWishlistButton extends HTMLElement {
  /** True while this frame's shared wishlist fetch is already queued. */
  static #fetchQueued = false;

  /**
   * Fetch the list once per frame for ALL buttons — N cart lines share one
   * Swym request. The callback merges into the shared set and repaints every
   * connected button.
   * @param {any} swat
   */
  static #fetchList(swat) {
    if (TSWishlistButton.#fetchQueued) return;
    TSWishlistButton.#fetchQueued = true;
    requestAnimationFrame(() => (TSWishlistButton.#fetchQueued = false));

    swat.fetch((/** @type {SwymProduct[]} */ products) => {
      const list = Array.isArray(products) ? products : [];
      // Merge only — a stale/partial list must not downgrade a saved item.
      for (const item of list) inWishlist.add(Number(item.epi));
      for (const el of document.querySelectorAll('ts-wishlist-button')) {
        if (el instanceof TSWishlistButton) el.#render();
      }
    });
  }

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {any} The Swym tracker (`window._swat`). */
  #swat = null;

  #inList = false;

  /** In-flight click guard — a field, not `disabled`, which morphs can strip mid-request. */
  #busy = false;

  /** @type {Element | null} */
  #drawer = null;

  /** @type {number | null} Pending coalesced-refresh frame handle. */
  #refreshHandle = null;

  /**
   * Re-asserts state after cart morphs: they rewrite our attributes to server
   * HTML (no wishlist state), some async with no event, wiping a correct paint.
   * @type {MutationObserver | null}
   */
  #morphObserver = null;

  connectedCallback() {
    // PDP wraps a `ts-button`; the cart line item uses its own icon+label button.
    this.#button = this.querySelector('.ts-button, button');
    const label = this.querySelector('.ts-button__label, [data-wishlist-label]');
    if (!this.#button || !label) return;

    // Paint known state immediately; the fetch confirms later.
    this.#render();

    this.#button.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(SWYM_REMOVED_EVENT, this.#onExternalRemove);
    // `dialog:open` doesn't bubble — listen on the drawer itself.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onRefreshEvent);
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener('dialog:open', this.#onRefreshEvent);

    this.#morphObserver = new MutationObserver(() => {
      // Own paints land here too — only react when the DOM disagrees with known state.
      const desired = inWishlist.has(Number(this.dataset.epi));
      if (this.hasAttribute('data-in-wishlist') === desired && this.#inList === desired) return;
      // A morph reset us (or re-pointed data-epi): repaint, then re-check with Swym.
      this.#render();
      this.#scheduleRefresh();
    });
    this.#morphObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-in-wishlist', 'data-epi'],
    });

    whenSwymReady(this.#onSwymReady);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(SWYM_REMOVED_EVENT, this.#onExternalRemove);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onRefreshEvent);
    this.#drawer?.removeEventListener('dialog:open', this.#onRefreshEvent);
    this.#morphObserver?.disconnect();
    this.#morphObserver = null;
    // Null the handle too — morphs move reused nodes (disconnect + reconnect in
    // one tick) and a stale handle would block #scheduleRefresh() forever.
    if (this.#refreshHandle != null) {
      cancelAnimationFrame(this.#refreshHandle);
      this.#refreshHandle = null;
    }

    // Don't let a late Swym load fire on a detached element.
    offSwymReady(this.#onSwymReady);
    this.#swat = null;
  }

  /**
   * Read fresh, never cache: morphs re-point this node at another product by
   * rewriting data-* without re-running connectedCallback.
   * @returns {SwymProduct}
   */
  #getProduct() {
    return {
      empi: Number(this.dataset.empi),
      epi: Number(this.dataset.epi),
      du: this.dataset.du ?? '',
      dt: this.dataset.dt,
      iu: this.dataset.iu,
      pr: Number(this.dataset.pr),
    };
  }

  /** @param {any} swat - The Swym tracker (`window._swat`). */
  #onSwymReady = (swat) => {
    this.#swat = swat;
    this.#scheduleRefresh();
  };

  /** Coalesce refresh triggers into one fetch on the next frame. */
  #scheduleRefresh() {
    if (this.#refreshHandle != null) return;
    this.#refreshHandle = requestAnimationFrame(() => {
      this.#refreshHandle = null;
      this.#refreshState();
    });
  }

  /**
   * Sync data-* to the newly selected variant. The event fires for every product
   * form on the page — only react to our own product.
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    // Cart lines are pinned to their line's variant — only the PDP button follows the picker.
    if (this.classList.contains('cart-items__wishlist')) return;

    const { resource, data } = /** @type {any} */ (event).detail ?? {};
    if (!resource) return;
    if (String(this.dataset.empi) !== String(data?.productId)) return;

    this.dataset.epi = String(resource.id);
    if (typeof resource.price === 'number') this.dataset.pr = String(resource.price / 100);
    const image = resource.featured_media?.preview_image?.src;
    if (image) {
      const url = new URL(image, window.location.origin);
      url.searchParams.set('width', '200');
      this.dataset.iu = url.toString();
    }

    this.#scheduleRefresh();
  };

  /** Re-check membership when the cart updates or the mini cart opens. */
  #onRefreshEvent = () => {
    this.#scheduleRefresh();
  };

  /**
   * A TS surface (wishlist page) removed an item — the one downgrade signal
   * fetches can't provide, since they merge only.
   * @param {Event} event
   */
  #onExternalRemove = (event) => {
    const epi = Number(/** @type {CustomEvent} */ (event).detail?.epi);
    if (!Number.isFinite(epi)) return;
    inWishlist.delete(epi);
    this.#render();
  };

  /** Request the shared fetch — it repaints all buttons, this one included. */
  #refreshState() {
    if (!this.#swat) return;
    TSWishlistButton.#fetchList(this.#swat);
  }

  /** Set state from the shared set and paint. */
  #render() {
    const epi = Number(this.dataset.epi);
    this.#inList = inWishlist.has(epi);
    this.#renderLabel();
  }

  #renderLabel() {
    // State hook: surfaces like ts-wishlist-icon-button fill their icon on it.
    this.toggleAttribute('data-in-wishlist', this.#inList);
    // Re-query — a morph can replace the label node.
    const label = this.querySelector('.ts-button__label, [data-wishlist-label]');
    if (!label) return;
    const { addLabel = '', removeLabel = '' } = this.dataset;
    label.textContent = this.#inList ? removeLabel : addLabel;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    event.preventDefault();
    if (this.#busy || !this.#swat || !this.#button) return;

    // Read fresh at click time — a morph may have re-pointed this node (see #getProduct).
    const product = this.#getProduct();
    // Liquid stamps the line key on removable cart lines — its presence is move mode.
    const lineKey = this.dataset.tsMove;
    this.#busy = true;
    this.#button.disabled = true;

    const settle = (/** @type {boolean} */ inList) => {
      // A click is the only source of truth for removal.
      if (inList) inWishlist.add(product.epi);
      else inWishlist.delete(product.epi);
      // Repaint every button for this variant — the PDP button and cart lines can coexist.
      for (const el of document.querySelectorAll(`ts-wishlist-button[data-epi="${product.epi}"]`)) {
        if (el instanceof TSWishlistButton) el.#render();
      }
      // Tell non-button surfaces (the wishlist page grid) about the toggle.
      const eventName = inList ? SWYM_ADDED_EVENT : SWYM_REMOVED_EVENT;
      document.dispatchEvent(new CustomEvent(eventName, { detail: { epi: product.epi, product } }));
      this.#busy = false;
      if (this.#button) this.#button.disabled = false;
      // Only a completed ADD moves the line out — a wishlist remove leaves the cart alone.
      if (lineKey && inList) this.#removeLine(lineKey);
    };
    const onError = (/** @type {unknown} */ error) => {
      console.error('[ts-wishlist-button] Swym request failed:', error);
      // Swym's own error UI is CSS-hidden — let the flyout surface the failure.
      document.dispatchEvent(new CustomEvent(SWYM_ERROR_EVENT, { detail: { epi: product.epi, product } }));
      this.#busy = false;
      if (this.#button) this.#button.disabled = false;
    };

    // If Swym's `ui`/method isn't ready, don't leave the button disabled forever.
    try {
      if (this.#inList && !lineKey) {
        this.#swat.removeFromWishList(product, () => settle(false), onError);
      } else if (typeof this.#swat.addToWishList === 'function') {
        // Headless add — the theme's own flyout (ts-wishlist-flyout, driven by
        // the ts:swym:added event settle() dispatches) replaces Swym's toast.
        // A move adds even when #inList: the set is merge-only so it can be
        // stale-true, and the idempotent add re-confirms membership before
        // settle() drops the cart line.
        this.#swat.addToWishList(product, () => settle(true), onError);
      } else {
        // Older Swym API without the headless method; its toast stays CSS-hidden.
        this.#swat.ui.performAddToWishlist(product, () => settle(true), onError);
      }
    } catch (error) {
      onError(error);
    }
  };

  /**
   * Move mode: drop the clicked cart line once the item is confirmed in the
   * wishlist, reusing the cart component's remove flow (row animation +
   * /cart/change), same as the delete button. The row is found by key at
   * removal time and the /cart/change request also targets the key (see
   * updateQuantity), so a concurrent cart change renumbering the lines can't
   * clear the wrong one; a vanished key (line already deleted) makes this a
   * no-op.
   * @param {string} key - The line item key captured at click time.
   */
  #removeLine(key) {
    const cartItems = /** @type {any} */ (this.closest('cart-items-component'));
    const rows = cartItems?.refs?.cartItemRows;
    if (!Array.isArray(rows) || typeof cartItems.onLineItemRemove !== 'function') return;
    const line = rows.findIndex((row) => row instanceof HTMLElement && row.dataset.key === key) + 1;
    if (line < 1) return;
    cartItems.onLineItemRemove(line);
  }
}

customElements.define('ts-wishlist-button', TSWishlistButton);
